import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { QRCodeSVG } from 'qrcode.react';
import { computeIsGermanDefault, getCurrentLangCode } from '../utils/language';

const WISE_STATIC_QR_URL = 'https://findyourbroker.ai/wp-content/uploads/2026/03/wiseQRcode_FMNAG.png';
const WISE_RECIPIENT_NAME = 'Financial Market Navigator AG';
const WISE_TAG = '@financialmarketnavigatorag';
const WISE_BUSINESS_PAGE_URL = 'https://wise.com/pay/business/financialmarketnavigatorag';

const MARKET_COUNTRIES = [
  { code: 'CH', name: 'Switzerland', nameDe: 'Schweiz' },
  { code: 'DE', name: 'Germany', nameDe: 'Deutschland' },
  { code: 'AT', name: 'Austria', nameDe: 'Österreich' },
  { code: 'FR', name: 'France', nameDe: 'Frankreich' },
  { code: 'IT', name: 'Italy', nameDe: 'Italien' }
];

const PAYMENT_METHODS = [
  { value: 'twint', label: 'TWINT', labelDe: 'TWINT' },
  { value: 'credit_card', label: 'Credit Card', labelDe: 'Kreditkarte' },
  { value: 'debit_card', label: 'Debit Card', labelDe: 'Debitkarte' },
  { value: 'payrexx', label: 'Payrexx', labelDe: 'Payrexx' },
  { value: 'wise', label: 'Wise', labelDe: 'Wise' }
];

function isLocalDevelopmentHost() {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male', labelDe: 'Männlich' },
  { value: 'female', label: 'Female', labelDe: 'Weiblich' },
  { value: 'other', label: 'Other', labelDe: 'Andere' }
];

function getRestBase() {
  if (typeof window !== 'undefined' && window.rypLoginConfig && window.rypLoginConfig.restUrl) {
    return window.rypLoginConfig.restUrl;
  }
  return (window.location.origin || '') + '/?rest_route=/ryp/v1/';
}

function buildRestUrl(endpoint) {
  const base = getRestBase();
  if (base.includes('rest_route=')) {
    return base.replace(/\/$/, '').replace(/\/ryp\/v1\/?$/, '') + '/ryp/v1/' + endpoint;
  }
  return base + endpoint;
}

function buildInvoiceStyleReference(orderId) {
  const safeId = Number.parseInt(String(orderId || ''), 10);
  const idPart = Number.isFinite(safeId) && safeId > 0 ? String(safeId).padStart(3, '0') : '000';
  const year = new Date().getUTCFullYear();
  return `B${year}100${idPart}`;
}

function getRequestedCurrency() {
  const allowed = ['CHF', 'EUR', 'USD'];
  const normalize = (value) => String(value || '').trim().toUpperCase();

  try {
    const params = new URLSearchParams(window.location.search || '');
    const fromQuery = normalize(params.get('currency'));
    if (allowed.includes(fromQuery)) return fromQuery;
  } catch (e) {
    // Ignore URL parsing issues and continue with storage/default.
  }

  const storageKeys = ['selectedCurrency', 'orderCurrency', 'currency'];
  for (const key of storageKeys) {
    const stored = normalize(localStorage.getItem(key));
    if (allowed.includes(stored)) return stored;
  }

  return 'CHF';
}

/**
 * Build a payment QR payload.
 * CHF → Swiss QR-bill format (SPC), EUR → EPC/SEPA format (EPC069-12).
 */
function buildPaymentQR(payIn) {
  if (!payIn) return null;
  const iban = (payIn.iban || '').replace(/\s/g, '');
  const amount = payIn.amount || '0.00';
  const currency = (payIn.currency || 'CHF').toUpperCase();
  const name = payIn.account_holder || '';
  const ref = payIn.reference || '';
  const bic = (payIn.bic || '').replace(/\s/g, '');

  if (!iban) return null;

  if (currency === 'CHF' && iban.startsWith('CH')) {
    // Swiss QR-bill (SPC) format
    return [
      'SPC',        // QR Type
      '0200',       // Version
      '1',          // Coding (UTF-8)
      iban,         // IBAN
      'K',          // Address type (K = combined)
      name,         // Name
      '',            // Street (optional combined)
      '',            // House number
      '',            // Postal code
      '',            // City
      'CH',         // Country
      '',            // Ultimate creditor (empty)
      '',            // ...
      '',            // ...
      '',            // ...
      '',            // ...
      '',            // ...
      amount,       // Amount
      currency,     // Currency
      '',            // Ultimate debtor
      '',            // ...
      '',            // ...
      '',            // ...
      '',            // ...
      '',            // ...
      'NON',        // Reference type (NON = no structured ref)
      '',            // Reference
      ref,          // Unstructured message
      'EPD',        // Trailer
    ].join('\n');
  }

  // EPC/SEPA QR Code (EPC069-12)
  return [
    'BCD',              // Service tag
    '002',              // Version
    '1',                // Character set (UTF-8)
    'SCT',              // Identification
    bic,                // BIC
    name,               // Beneficiary name
    iban,               // IBAN
    currency + amount,  // Amount (e.g. EUR100.00)
    '',                 // Purpose
    ref,                // Remittance reference
    '',                 // Remittance text
    '',                 // Information
  ].join('\n');
}

export default function ClientDetailsPayment({ setCurrentStep }) {
  const [isGerman, setIsGerman] = useState(() => computeIsGermanDefault());
  const isPolish = getCurrentLangCode() === 'pl';
  const [view, setView] = useState('form'); // 'form' | 'payment_instructions'
  const [orderData, setOrderData] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => getRequestedCurrency());
  const [formData, setFormData] = useState({
    paymentMethod: '',
    companyName: '',
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    country: '',
    zipCode: '',
    town: '',
    street: '',
    houseNumber: '',
    telephone: '',
    paymentDetails: '',
    wiseUsername: '',
    wiseAccountConfirmed: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const visiblePaymentMethods = PAYMENT_METHODS.filter(
    (method) => method.value !== 'payrexx' || isLocalDevelopmentHost()
  );

  useEffect(() => {
    setIsGerman(computeIsGermanDefault());
    setSelectedCurrency(getRequestedCurrency());
    const userEmail = localStorage.getItem('user_email');
    const userFirstName = localStorage.getItem('user_first_name') || '';
    const userLastName = localStorage.getItem('user_last_name') || '';
    if (userEmail || userFirstName || userLastName) {
      setFormData(prev => ({
        ...prev,
        email: prev.email || userEmail || '',
        firstName: prev.firstName || userFirstName || '',
        lastName: prev.lastName || userLastName || ''
      }));
    }
  }, []);

  useEffect(() => {
    const normalized = String(selectedCurrency || 'CHF').toUpperCase();
    localStorage.setItem('selectedCurrency', normalized);
    localStorage.setItem('orderCurrency', normalized);
    localStorage.setItem('currency', normalized);
  }, [selectedCurrency]);

  const getText = useCallback((key) => {
    const texts = isPolish ? {
      title: 'Dane klienta i płatność',
      subtitle: 'Proszę uzupełnić swoje dane',
      back: 'Wstecz',
      paymentMethod: 'Metoda płatności',
      paymentMethodRequired: 'Wybór metody płatności jest wymagany.',
      companyName: 'Nazwa firmy',
      firstName: 'Imię',
      firstNameRequired: 'Imię jest wymagane (jeśli nie podano nazwy firmy).',
      lastName: 'Nazwisko',
      lastNameRequired: 'Nazwisko jest wymagane (jeśli nie podano nazwy firmy).',
      gender: 'Płeć',
      genderRequired: 'Płeć jest wymagana (jeśli podano nazwisko).',
      email: 'Adres e-mail',
      emailRequired: 'Adres e-mail jest wymagany.',
      emailInvalid: 'Nieprawidłowy adres e-mail.',
      address: 'Adres',
      country: 'Kraj',
      countryRequired: 'Kraj jest wymagany.',
      zipCode: 'Kod pocztowy',
      zipCodeRequired: 'Kod pocztowy jest wymagany.',
      town: 'Miasto',
      street: 'Ulica',
      streetRequired: 'Ulica jest wymagana.',
      streetInvalid: 'Podaj poprawną ulicę (min. 3 znaki, co najmniej jedna litera).',
      houseNumber: 'Numer domu',
      houseNumberRequired: 'Numer domu jest wymagany.',
      houseNumberInvalid: 'Podaj poprawny numer domu (np. 12 lub 12A).',
      telephone: 'Numer telefonu',
      telephoneRequired: 'Numer telefonu jest wymagany (dla TWINT).',
      telephoneInvalid: 'Numer telefonu musi być zgodny z wybranym krajem.',
      paymentDetails: 'Dane płatności',
      paymentDetailsRequired: 'Dane płatności są wymagane.',
      creditCardNumber: 'Numer karty kredytowej',
      bankAccount: 'Numer konta',
      wiseReference: 'Referencja Wise (e-mail lub ID transferu)',
      wiseUsername: 'Nazwa użytkownika Wise lub e-mail',
      wiseUsernameRequired: 'Nazwa użytkownika lub e-mail Wise jest wymagana.',
      wiseAccountConfirm: 'Potwierdzam, że mam konto Wise',
      wiseAccountConfirmRequired: 'Potwierdzenie posiadania konta Wise jest wymagane.',
      twintReferenceOptional: 'Referencja TWINT (opcjonalnie)',
      payNow: 'Przejdź do płatności',
      paymentCurrency: 'Waluta płatności',
      validationError: 'Popraw błędy w formularzu.',
      processing: 'Tworzenie zamówienia...',
      oneOfRequired: 'Wypełnij nazwę firmy albo imię i nazwisko.',
      payInstructionsTitle: 'Instrukcje płatności',
      payInstructionsSubtitle: 'Proszę przelać poniższą kwotę',
      amount: 'Kwota',
      recipient: 'Odbiorca',
      iban: 'IBAN',
      bic: 'BIC',
      reference: 'Tytuł przelewu',
      bankName: 'Nazwa banku',
      accountNumber: 'Numer konta',
      accountType: 'Typ konta',
      routingNumber: 'Numer rozliczeniowy',
      swiftBic: 'SWIFT/BIC',
      bankAddress: 'Adres banku',
      transferNotes: 'Uwagi',
      importantNote: 'Ważne: użyj dokładnego kodu referencyjnego, aby płatność mogła zostać poprawnie przypisana.',
      copyAll: 'Kopiuj wszystkie dane',
      copied: 'Skopiowano!',
      ivePaid: 'Zapłaciłem',
      confirmingPayment: 'Potwierdzanie płatności...',
      orderCreated: 'Zamówienie utworzone',
      orderNumber: 'Numer zamówienia',
      demoNote: 'Tryb demo — nie wykonano żadnego rzeczywistego transferu pieniędzy.',
      scanQR: 'Zeskanuj w aplikacji bankowej',
      qrAmountNote: 'Zeskanuj ten kod QR w aplikacji Wise i wpisz ręcznie dokładną kwotę CHF.',
      qrPayment: 'Płatność QR',
      bankTransfer: 'Przelew bankowy',
      companyOrPersonalDetails: 'Dane firmowe lub osobowe',
      orSeparator: 'LUB',
      wiseInfoMessage: 'Po wysłaniu formularza otrzymasz dane bankowe do realizacji płatności przez Wise.',
      orderCreationFailed: 'Nie udało się utworzyć zamówienia.',
      connectionError: 'Błąd połączenia. Spróbuj ponownie.',
      confirmationFailed: 'Potwierdzenie płatności nie powiodło się.'
    } : (isGerman ? {
      title: 'Kundendaten und Zahlung',
      subtitle: 'Bitte tragen Sie Ihre Daten ein',
      back: 'Zurück',
      paymentMethod: 'Zahlungsmethode',
      paymentMethodRequired: 'Zahlungsmethode ist erforderlich',
      companyName: 'Firmenname',
      firstName: 'Vorname',
      firstNameRequired: 'Vorname ist erforderlich (wenn kein Firmenname)',
      lastName: 'Nachname',
      lastNameRequired: 'Nachname ist erforderlich (wenn kein Firmenname)',
      gender: 'Geschlecht',
      genderRequired: 'Geschlecht ist erforderlich (wenn Nachname ausgefüllt)',
      email: 'E-Mail-Adresse',
      emailRequired: 'E-Mail-Adresse ist erforderlich',
      emailInvalid: 'Ungültige E-Mail-Adresse',
      address: 'Adresse',
      country: 'Land',
      countryRequired: 'Land ist erforderlich',
      zipCode: 'Postleitzahl',
      zipCodeRequired: 'Postleitzahl ist erforderlich',
      town: 'Stadt',
      street: 'Straße',
      streetRequired: 'Straße ist erforderlich',
      streetInvalid: 'Bitte geben Sie eine gültige Straße ein (mind. 3 Zeichen, mindestens ein Buchstabe)',
      houseNumber: 'Hausnummer',
      houseNumberRequired: 'Hausnummer ist erforderlich',
      houseNumberInvalid: 'Bitte geben Sie eine gültige Hausnummer ein (z.B. 12 oder 12A)',
      telephone: 'Telefonnummer',
      telephoneRequired: 'Telefonnummer ist erforderlich (für TWINT)',
      telephoneInvalid: 'Telefonnummer muss dem Land entsprechen',
      paymentDetails: 'Zahlungsdetails',
      paymentDetailsRequired: 'Zahlungsdetails sind erforderlich',
      creditCardNumber: 'Kreditkartennummer',
      bankAccount: 'Bankkontonummer',
      wiseReference: 'Wise Referenz (E-Mail oder Transfer-ID)',
      wiseUsername: 'Wise Benutzername oder E-Mail',
      wiseUsernameRequired: 'Wise Benutzername/E-Mail ist erforderlich',
      wiseAccountConfirm: 'Ich bestätige, dass ich ein Wise Konto habe',
      wiseAccountConfirmRequired: 'Bestätigung für Wise Konto ist erforderlich',
      twintReferenceOptional: 'TWINT Referenz (optional)',
      payNow: 'Jetzt bezahlen',
      paymentCurrency: 'Zahlungswährung',
      validationError: 'Bitte korrigieren Sie die Fehler im Formular',
      processing: 'Bestellung wird erstellt...',
      oneOfRequired: 'Entweder Firmenname oder Vor- und Nachname müssen ausgefüllt werden',
      // Payment instructions texts
      payInstructionsTitle: 'Zahlungsanweisungen',
      payInstructionsSubtitle: 'Bitte überweisen Sie den folgenden Betrag',
      amount: 'Betrag',
      recipient: 'Empfänger',
      iban: 'IBAN',
      bic: 'BIC',
      reference: 'Verwendungszweck',
      bankName: 'Bank',
      accountNumber: 'Kontonummer',
      accountType: 'Kontotyp',
      routingNumber: 'Routing-Nummer',
      swiftBic: 'SWIFT/BIC',
      bankAddress: 'Bankadresse',
      transferNotes: 'Hinweise',
      wiseTag: 'Wise Tag',
      wisePage: 'Wise Seite',
      openWisePage: 'Wise Seite öffnen',
      importantNote: 'Wichtig: Bitte verwenden Sie den exakten Verwendungszweck, damit Ihre Zahlung zugeordnet werden kann.',
      copyAll: 'Alle Details kopieren',
      copied: 'Kopiert!',
      ivePaid: 'Ich habe bezahlt',
      confirmingPayment: 'Zahlung wird bestätigt...',
      orderCreated: 'Bestellung erstellt',
      orderNumber: 'Bestellnummer',
      demoNote: 'Demo-Modus – es wurde kein echtes Geld bewegt.',
      scanQR: 'Mit Banking-App scannen',
      qrAmountNote: 'Scannen Sie den QR-Code in der Wise App und geben Sie den exakten CHF-Betrag manuell ein.',
      qrPayment: 'QR-Zahlung',
    } : {
      title: 'Client Details and Payment',
      subtitle: 'Please fill in your details',
      back: 'Back',
      paymentMethod: 'Payment Method',
      paymentMethodRequired: 'Payment method is required',
      companyName: 'Company Name',
      firstName: 'First Name',
      firstNameRequired: 'First name is required (if no company name)',
      lastName: 'Last Name',
      lastNameRequired: 'Last name is required (if no company name)',
      gender: 'Gender',
      genderRequired: 'Gender is required (if last name is filled)',
      email: 'Email Address',
      emailRequired: 'Email address is required',
      emailInvalid: 'Invalid email address',
      address: 'Address',
      country: 'Country',
      countryRequired: 'Country is required',
      zipCode: 'Zip Code',
      zipCodeRequired: 'Zip code is required',
      town: 'Town',
      street: 'Street',
      streetRequired: 'Street is required',
      streetInvalid: 'Please enter a valid street (min. 3 characters, at least one letter)',
      houseNumber: 'House Number',
      houseNumberRequired: 'House number is required',
      houseNumberInvalid: 'Please enter a valid house number (e.g. 12 or 12A)',
      telephone: 'Telephone Number',
      telephoneRequired: 'Telephone number is required (for TWINT)',
      telephoneInvalid: 'Telephone number must match country',
      paymentDetails: 'Payment Details',
      paymentDetailsRequired: 'Payment details are required',
      creditCardNumber: 'Credit Card Number',
      bankAccount: 'Account Number',
      wiseReference: 'Wise reference (email or transfer ID)',
      wiseUsername: 'Wise username or email',
      wiseUsernameRequired: 'Wise username/email is required',
      wiseAccountConfirm: 'I confirm that I have a Wise account',
      wiseAccountConfirmRequired: 'Confirmation of Wise account is required',
      twintReferenceOptional: 'TWINT reference (optional)',
      payNow: 'Pay Now',
      paymentCurrency: 'Payment Currency',
      validationError: 'Please correct the errors in the form',
      processing: 'Creating order...',
      oneOfRequired: 'Either company name or first name and last name must be filled',
      payInstructionsTitle: 'Payment Instructions',
      payInstructionsSubtitle: 'Please transfer the following amount',
      amount: 'Amount',
      recipient: 'Recipient',
      iban: 'IBAN',
      bic: 'BIC',
      reference: 'Reference',
      bankName: 'Bank',
      accountNumber: 'Account Number',
      accountType: 'Account Type',
      routingNumber: 'Routing Number',
      swiftBic: 'SWIFT/BIC',
      bankAddress: 'Bank Address',
      transferNotes: 'Notes',
      wiseTag: 'Wise tag',
      wisePage: 'Wise page',
      openWisePage: 'Open Wise page',
      importantNote: 'Important: Please use the exact reference code so your payment can be matched.',
      copyAll: 'Copy all details',
      copied: 'Copied!',
      ivePaid: "I've paid",
      confirmingPayment: 'Confirming payment...',
      orderCreated: 'Order created',
      orderNumber: 'Order number',
      demoNote: 'Demo mode – no real money was moved.',
      scanQR: 'Scan with your banking app',
      qrAmountNote: 'Scan this QR code in the Wise app and enter the exact CHF amount manually.',
      qrPayment: 'QR Payment',
    });
    return texts[key] || key;
  }, [isGerman, isPolish]);

  const handleChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'wiseAccountConfirmed') {
      value = !!event.target.checked;
    }
    if (field === 'paymentDetails' && formData.paymentMethod === 'credit_card') {
      value = value.replace(/[^0-9\s]/g, '');
    }
    if (field === 'paymentDetails' && formData.paymentMethod === 'debit_card') {
      value = value.replace(/[^a-zA-Z0-9\s]/g, '').toUpperCase();
    }
    if (field === 'zipCode') {
      value = value.replace(/[^0-9]/g, '');
    }
    if (field === 'houseNumber') {
      value = value.replace(/[^a-zA-Z0-9]/g, '');
    }
    if (field === 'street') {
      value = value.replace(/[^a-zA-Z\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00e9\u00e8\u00ea\u00e0\u00e2\u00ee\u00f4\u00fb\u00df\s.\-0-9]/g, '');
    }
    if (field === 'town') {
      value = value.replace(/[^a-zA-Z\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00e9\u00e8\u00ea\u00e0\u00e2\u00ee\u00f4\u00fb\u00df\s.\-]/g, '');
    }
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00e9\u00e8\u00ea\u00e0\u00e2\u00ee\u00f4\u00fb\u00df\s\-']/g, '');
    }
    if (field === 'companyName') {
      value = value.replace(/[^a-zA-Z0-9\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00df\s.\-&+,()]/g, '');
    }
    if (field === 'telephone') {
      value = value.replace(/[^0-9+\s\-()/]/g, '');
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCurrencySelect = (currency) => {
    const normalized = String(currency || 'CHF').toUpperCase();
    if (normalized === 'CHF' || normalized === 'EUR' || normalized === 'USD') {
      setSelectedCurrency(normalized);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = getText('paymentMethodRequired');
    }
    const hasCompanyName = formData.companyName.trim() !== '';
    const hasFirstName = formData.firstName.trim() !== '';
    const hasLastName = formData.lastName.trim() !== '';
    if (!hasCompanyName && (!hasFirstName || !hasLastName)) {
      newErrors.companyName = getText('oneOfRequired');
      if (!hasFirstName) newErrors.firstName = getText('firstNameRequired');
      if (!hasLastName) newErrors.lastName = getText('lastNameRequired');
    }
    if (!hasCompanyName && hasFirstName && !hasLastName) {
      newErrors.lastName = getText('lastNameRequired');
    }
    if (!formData.email) {
      newErrors.email = getText('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = getText('emailInvalid');
    }
    if (!formData.country) {
      newErrors.country = getText('countryRequired');
    }
    if (!formData.zipCode) {
      newErrors.zipCode = getText('zipCodeRequired');
    }
    if (!formData.street) {
      newErrors.street = getText('streetRequired');
    } else {
      const streetValue = String(formData.street).trim().replace(/\s+/g, ' ');
      if (streetValue.length < 3 || !/[a-zA-Z\u00e4\u00f6\u00fc\u00c4\u00d6\u00dc\u00e9\u00e8\u00ea\u00e0\u00e2\u00ee\u00f4\u00fb\u00df]/.test(streetValue)) {
        newErrors.street = getText('streetInvalid');
      }
    }
    if (!formData.houseNumber) {
      newErrors.houseNumber = getText('houseNumberRequired');
    } else {
      const houseValue = String(formData.houseNumber).trim();
      if (houseValue.length > 10 || !/^(?=.*\d)[a-zA-Z0-9]+$/.test(houseValue)) {
        newErrors.houseNumber = getText('houseNumberInvalid');
      }
    }
    if (formData.paymentMethod === 'twint' && !formData.telephone) {
      newErrors.telephone = getText('telephoneRequired');
    }
    if (formData.paymentMethod === 'wise') {
      if (!formData.wiseUsername || formData.wiseUsername.trim().length < 3) {
        newErrors.wiseUsername = getText('wiseUsernameRequired');
      }
      if (!formData.wiseAccountConfirmed) {
        newErrors.wiseAccountConfirmed = getText('wiseAccountConfirmRequired');
      }
    }
    // Wise/Payrexx don't need paymentDetails (server-side flow handles instructions/checkout)
    if (formData.paymentMethod !== 'twint' && formData.paymentMethod !== 'wise' && formData.paymentMethod !== 'payrexx' && !formData.paymentDetails) {
      newErrors.paymentDetails = getText('paymentDetailsRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const product = localStorage.getItem('selectedProduct') || 'standard';
      const originalPrice = parseFloat(localStorage.getItem('selectedProductPrice') || '100');
      const promoRaw = localStorage.getItem('promoDiscount');
      const promoCode = localStorage.getItem('promoCode') || '';
      const parsedPromo = promoRaw ? JSON.parse(promoRaw) : null;
      const promoMatchesCurrentSelection = !!(
        promoCode &&
        parsedPromo &&
        parsedPromo.product === product &&
        Number(parsedPromo.basePrice) === Number(originalPrice) &&
        parsedPromo.code === promoCode
      );
      const promoDiscount = promoMatchesCurrentSelection ? parsedPromo : null;
      const productPrice = promoDiscount ? Number(promoDiscount.finalPrice) : originalPrice;
      const orderCurrency = selectedCurrency || getRequestedCurrency();

      const url = buildRestUrl('orders');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
        },
        body: JSON.stringify({
          token: token,
          product: product,
          amount: productPrice,
          currency: orderCurrency,
          payment_method: formData.paymentMethod,
          client_details: formData,
          promo_code: promoCode || undefined,
          original_amount: promoDiscount ? originalPrice : undefined,
          discount_percent: promoDiscount ? promoDiscount.percent : undefined,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setErrors({ submit: data.message || getText('orderCreationFailed') });
        setIsSubmitting(false);
        return;
      }

      // Store order info
      const paymentData = {
        ...formData,
        orderId: data.order_id,
        timestamp: new Date().toISOString(),
        product: product,
        productPrice: Number(data.amount || productPrice),
        currency: (data.pay_in && data.pay_in.currency) || orderCurrency,
        paymentMethod: formData.paymentMethod,
        status: data.status,
      };
      localStorage.setItem('paymentData', JSON.stringify(paymentData));
      localStorage.setItem('lastOrderId', String(data.order_id));

      if (formData.paymentMethod === 'wise') {
        const payIn = data.pay_in || {};
        const fallbackPayIn = {
          type: payIn.type || 'bank_transfer',
          bank_name: payIn.bank_name || 'Wise Switzerland AG',
          account_holder: payIn.account_holder || WISE_RECIPIENT_NAME,
          iban: payIn.iban || '',
          bic: payIn.bic || '',
          swift_bic: payIn.swift_bic || '',
          account_number: payIn.account_number || '',
          account_type: payIn.account_type || '',
          routing_number: payIn.routing_number || '',
          bank_address: payIn.bank_address || '',
          transfer_notes: Array.isArray(payIn.transfer_notes) ? payIn.transfer_notes : [],
          reference: payIn.reference || buildInvoiceStyleReference(data.order_id),
          amount: payIn.amount || Number(data.amount || productPrice).toFixed(2),
          currency: payIn.currency || orderCurrency,
          message: payIn.message || '',
        };
        setOrderData({
          orderId: data.order_id,
          status: data.status,
          payIn: fallbackPayIn,
          product: product,
          amount: Number(data.amount || productPrice),
          wiseUsername: formData.wiseUsername,
        });
        setView('payment_instructions');
        setIsSubmitting(false);
      } else if (formData.paymentMethod === 'payrexx') {
        const checkoutUrl = data.payrexx_checkout_url || (data.pay_in && data.pay_in.checkout_url);
        if (checkoutUrl && typeof window !== 'undefined') {
          // Localhost + VPOS workflow for QA:
          // keep full Payrexx/PayPal journey in a new tab, then continue to Billing in this tab
          // as soon as the payment tab is closed.
          if (isLocalDevelopmentHost() && /\/vpos(\?|$)/i.test(String(checkoutUrl))) {
            // Open a blank tab first (user gesture) to reduce popup-blocker false positives,
            // then navigate that tab to Payrexx VPOS.
            let popup = null;
            try {
              popup = window.open('', '_blank');
              if (popup) {
                try { popup.opener = null; } catch (e) { /* ignore */ }
                popup.location.href = checkoutUrl;
              }
            } catch (e) {
              popup = null;
            }
            if (popup) {
              try {
                const existing = JSON.parse(localStorage.getItem('paymentData') || '{}');
                existing.status = 'paid';
                existing.paymentMethod = 'payrexx';
                existing.timestamp = new Date().toISOString();
                localStorage.setItem('paymentData', JSON.stringify(existing));
              } catch (e) {
                // Ignore malformed localStorage.
              }
              // Localhost test mode: continue UI immediately without waiting for external confirmation.
              // Use both React step update and URL navigation for reliability across rerenders.
              setCurrentStep({ activeStep: 4, status: 'paid' });
              try {
                const origin = window.location.origin || '';
                const params = new URLSearchParams(window.location.search || '');
                const search = new URLSearchParams();
                search.set('page_id', '63');
                search.set('resume_checkout', '1');
                search.set('payrexx', 'success');
                const lang = params.get('lang');
                if (lang) search.set('lang', lang);
                window.setTimeout(() => {
                  window.location.href = origin + '/?' + search.toString();
                }, 50);
              } catch (e) {
                // Ignore URL parsing issues; React step update above is the fallback.
              }
              setIsSubmitting(false);
              return;
            }
            // Do not fall back to same-tab on localhost test mode; that causes "stuck on Payrexx result" UX.
            setErrors({ submit: 'Popup blocked by browser. Please allow popups for localhost to continue Payrexx test flow.' });
            setIsSubmitting(false);
            return;
          }
          // Localhost only: same-tab Payrexx → PayPal → return can drop resume_checkout; flag for UploadFile resume.
          if (isLocalDevelopmentHost()) {
            try {
              sessionStorage.setItem('ryp_payrexx_return_expected', '1');
              sessionStorage.setItem('ryp_payrexx_return_at', String(Date.now()));
            } catch (e) {
              /* ignore */
            }
          }
          window.location.href = checkoutUrl;
          return;
        }
        setErrors({ submit: 'Payrexx checkout URL is missing from server response.' });
        setIsSubmitting(false);
      } else {
        setCurrentStep({ activeStep: 4, status: 'payment-success' });
      }
    } catch (error) {
      console.error('Order creation error:', error);
      setErrors({ submit: getText('connectionError') });
      setIsSubmitting(false);
    }
  };

  const handleCopyDetails = () => {
    if (!orderData || !orderData.payIn) return;
    const p = orderData.payIn;
    const swiftValue = p.swift_bic || p.bic || '';
    const lines = [
      `${getText('recipient')}: ${p.account_holder || ''}`,
      `${getText('wiseTag')}: ${WISE_TAG}`,
      `${getText('wisePage')}: ${WISE_BUSINESS_PAGE_URL}`,
      `${getText('amount')}: ${p.amount} ${p.currency}`,
      `${getText('reference')}: ${p.reference || ''}`,
      `${getText('bankName')}: ${p.bank_name || ''}`,
      `${getText('iban')}: ${p.iban || ''}`,
      `${getText('bic')}: ${p.bic || ''}`,
      `${getText('swiftBic')}: ${swiftValue}`,
      `${getText('accountNumber')}: ${p.account_number || ''}`,
      `${getText('accountType')}: ${p.account_type || ''}`,
      `${getText('routingNumber')}: ${p.routing_number || ''}`,
      `${getText('bankAddress')}: ${p.bank_address || ''}`,
    ]
      .concat((Array.isArray(p.transfer_notes) ? p.transfer_notes : []).map((note) => `${getText('transferNotes')}: ${note || ''}`))
      .filter(l => !l.endsWith(': '))
      .join('\n');
    navigator.clipboard.writeText(lines).then(() => setCopySnackbar(true));
  };

  const handleConfirmPaid = async () => {
    if (!orderData) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = buildRestUrl('orders/' + orderData.orderId + '/confirm');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
        },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (data.success) {
        const existing = JSON.parse(localStorage.getItem('paymentData') || '{}');
        existing.status = data.status || existing.status || 'paid';
        localStorage.setItem('paymentData', JSON.stringify(existing));
        setCurrentStep({ activeStep: 4, status: 'payment-success' });
      } else {
        setErrors({ submit: data.message || getText('confirmationFailed') });
      }
    } catch (error) {
      setErrors({ submit: getText('connectionError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompanyNameFilled = formData.companyName.trim() !== '';
  const isLastNameFilled = formData.lastName.trim() !== '';

  // ── Payment Instructions View (Wise) ────────────────────────────────
  if (view === 'payment_instructions' && orderData) {
    const p = orderData.payIn || {};
    const isDemo = !!p.message && p.message.toLowerCase().includes('demo');
    const isChfPayment = String(p.currency || 'CHF').toUpperCase() === 'CHF';
    const swiftValue = p.swift_bic || p.bic || '';
    return (
      <Box sx={{ maxWidth: 700, margin: '0 auto', padding: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="text"
            onClick={() => setView('form')}
            sx={{ textTransform: 'none' }}
          >
            {getText('back')}
          </Button>
        </Box>

        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 1 }}>
          {getText('payInstructionsTitle')}
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
          {getText('payInstructionsSubtitle')}
        </Typography>

        <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 3 }}>
          {getText('orderCreated')} — {getText('orderNumber')}: <strong>#{orderData.orderId}</strong>
        </Alert>

        {isDemo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {getText('demoNote')}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccountBalanceIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                {getText('bankTransfer')}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {p.account_holder && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">{getText('recipient')}</Typography>
                  <Typography variant="body1" fontWeight={600}>{p.account_holder}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">{getText('wiseTag')}</Typography>
                <Typography variant="body1" fontWeight={600}>{WISE_TAG}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">{getText('wisePage')}</Typography>
                <Typography
                  component="a"
                  href={WISE_BUSINESS_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    display: 'block',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.4,
                  }}
                  title={WISE_BUSINESS_PAGE_URL}
                >
                  {getText('openWisePage')}
                </Typography>
              </Grid>
              {p.iban && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('iban')}</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                    {p.iban}
                  </Typography>
                </Grid>
              )}
              {p.bic && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('bic')}</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{p.bic}</Typography>
                </Grid>
              )}
              {p.account_number && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('accountNumber')}</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{p.account_number}</Typography>
                </Grid>
              )}
              {p.account_type && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('accountType')}</Typography>
                  <Typography variant="body1" fontWeight={600}>{p.account_type}</Typography>
                </Grid>
              )}
              {p.routing_number && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('routingNumber')}</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{p.routing_number}</Typography>
                </Grid>
              )}
              {swiftValue && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('swiftBic')}</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{swiftValue}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">{getText('amount')}</Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {p.amount || orderData.amount} {p.currency || 'CHF'}
                </Typography>
              </Grid>
              {p.reference && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">{getText('reference')}</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{
                    fontFamily: 'monospace',
                    backgroundColor: '#fff3cd',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    {p.reference}
                  </Typography>
                </Grid>
              )}
              {p.bank_name && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">{getText('bankName')}</Typography>
                  <Typography variant="body1">{p.bank_name}</Typography>
                </Grid>
              )}
              {p.bank_address && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">{getText('bankAddress')}</Typography>
                  <Typography variant="body1">{p.bank_address}</Typography>
                </Grid>
              )}
              {Array.isArray(p.transfer_notes) && p.transfer_notes.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{getText('transferNotes')}</Typography>
                  {p.transfer_notes.map((note, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary">
                      - {note}
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>

            <Alert severity="warning" sx={{ mt: 3 }}>
              {getText('importantNote')}
            </Alert>
          </CardContent>
        </Card>

        {isChfPayment && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <QrCode2Icon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{getText('qrPayment')}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
              <Box
                component="img"
                src={WISE_STATIC_QR_URL}
                alt="Wise payment QR"
                sx={{
                  width: 220,
                  height: 220,
                  objectFit: 'contain',
                  borderRadius: 2,
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#fff',
                  p: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {getText('scanQR')}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, maxWidth: 420 }}>
                {getText('qrAmountNote')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        )}

        {/* QR Code Card */}
        {(() => {
          if (WISE_STATIC_QR_URL) return null;
          const qrPayload = buildPaymentQR(p);
          if (!qrPayload) return null;
          return (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <QrCode2Icon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{getText('qrPayment')}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    border: '2px solid #e5e7eb',
                    display: 'inline-block',
                  }}>
                    <QRCodeSVG
                      value={qrPayload}
                      size={200}
                      level="M"
                      includeMargin={false}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {getText('scanQR')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          );
        })()}

        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.submit}</Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyDetails}
            sx={{ textTransform: 'none' }}
          >
            {getText('copyAll')}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
            onClick={handleConfirmPaid}
            disabled={isSubmitting}
            sx={{ minWidth: 200, textTransform: 'none' }}
          >
            {isSubmitting ? getText('confirmingPayment') : getText('ivePaid')}
          </Button>
        </Box>

        <Snackbar
          open={copySnackbar}
          autoHideDuration={2000}
          onClose={() => setCopySnackbar(false)}
          message={getText('copied')}
        />
      </Box>
    );
  }

  // ── Form View ───────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => setCurrentStep({ activeStep: 2, status: 'back-from-payment' })}
          sx={{ textTransform: 'none' }}
        >
          {getText('back')}
        </Button>
      </Box>

      <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 2 }}>
        {getText('title')}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {getText('subtitle')}
      </Typography>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Payment Method */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.paymentMethod}>
                  <FormLabel component="legend">{getText('paymentMethod')} *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.paymentMethod}
                    onChange={handleChange('paymentMethod')}
                  >
                    {visiblePaymentMethods.map((method) => (
                      <FormControlLabel
                        key={method.value}
                        value={method.value}
                        control={<Radio />}
                        label={isPolish
                          ? (method.value === 'credit_card'
                            ? 'Karta kredytowa'
                            : (method.value === 'debit_card'
                              ? 'Karta debetowa'
                              : (method.value === 'twint'
                                ? 'TWINT'
                                : (method.value === 'payrexx' ? 'Payrexx' : 'Wise'))))
                          : (isGerman ? method.labelDe : method.label)}
                      />
                    ))}
                  </RadioGroup>
                  {errors.paymentMethod && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.paymentMethod}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Wise-specific fields (placed directly under payment method) */}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">{getText('paymentCurrency')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {['CHF', 'EUR', 'USD'].map((currencyCode) => (
                    <Button
                      key={currencyCode}
                      type="button"
                      size="small"
                      variant={selectedCurrency === currencyCode ? 'contained' : 'outlined'}
                      onClick={() => handleCurrencySelect(currencyCode)}
                      sx={{ minWidth: 78, textTransform: 'none' }}
                    >
                      {currencyCode}
                    </Button>
                  ))}
                </Box>
              </Grid>

              {/* Wise-specific fields (placed directly under payment method) */}
              {formData.paymentMethod === 'wise' && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccountBalanceIcon />}>
                      {getText('wiseInfoMessage')}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      label={getText('wiseUsername')}
                      value={formData.wiseUsername}
                      onChange={handleChange('wiseUsername')}
                      error={!!errors.wiseUsername}
                      helperText={errors.wiseUsername}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.wiseAccountConfirmed}
                          onChange={handleChange('wiseAccountConfirmed')}
                        />
                      }
                      label={getText('wiseAccountConfirm')}
                      sx={{ mt: 1 }}
                    />
                    {errors.wiseAccountConfirmed && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {errors.wiseAccountConfirmed}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}

              {/* Company Name OR Personal Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {getText('companyOrPersonalDetails')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={getText('companyName')}
                  value={formData.companyName}
                  onChange={handleChange('companyName')}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {getText('orSeparator')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={getText('firstName')}
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  disabled={isCompanyNameFilled}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={getText('lastName')}
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  disabled={isCompanyNameFilled}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={!!errors.gender}>
                  <InputLabel>{getText('gender')}</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange('gender')}
                    label={getText('gender')}
                    disabled={isCompanyNameFilled || !isLastNameFilled}
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {isPolish
                          ? (option.value === 'male'
                            ? 'Mężczyzna'
                            : (option.value === 'female' ? 'Kobieta' : 'Inna'))
                          : (isGerman ? option.labelDe : option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label={getText('email')}
                  value={formData.email}
                  onChange={handleChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {getText('address')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.country}>
                  <InputLabel>{getText('country')} *</InputLabel>
                  <Select
                    value={formData.country}
                    onChange={handleChange('country')}
                    label={`${getText('country')} *`}
                  >
                    {MARKET_COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {isPolish
                          ? (country.code === 'CH'
                            ? 'Szwajcaria'
                            : (country.code === 'DE'
                              ? 'Niemcy'
                              : (country.code === 'AT'
                                ? 'Austria'
                                : (country.code === 'FR' ? 'Francja' : 'Włochy'))))
                          : (isGerman ? country.nameDe : country.name)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.country && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.country}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={getText('zipCode')}
                  value={formData.zipCode}
                  onChange={handleChange('zipCode')}
                  error={!!errors.zipCode}
                  helperText={errors.zipCode}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={getText('town')}
                  value={formData.town}
                  onChange={handleChange('town')}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label={getText('street')}
                  value={formData.street}
                  onChange={handleChange('street')}
                  error={!!errors.street}
                  helperText={errors.street}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={getText('houseNumber')}
                  InputLabelProps={{ shrink: true }}
                  value={formData.houseNumber}
                  onChange={handleChange('houseNumber')}
                  error={!!errors.houseNumber}
                  helperText={errors.houseNumber}
                  required
                />
              </Grid>

              {/* Telephone */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="tel"
                  label={getText('telephone')}
                  value={formData.telephone}
                  onChange={handleChange('telephone')}
                  error={!!errors.telephone}
                  helperText={errors.telephone || (formData.paymentMethod === 'twint' ? getText('telephoneRequired') : '')}
                  required={formData.paymentMethod === 'twint'}
                />
              </Grid>

              {/* Payment Details — hidden for Wise/Payrexx (server-side payment flow) */}
              {formData.paymentMethod !== 'wise' && formData.paymentMethod !== 'payrexx' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      formData.paymentMethod === 'credit_card'
                        ? getText('creditCardNumber')
                        : formData.paymentMethod === 'twint'
                          ? getText('twintReferenceOptional')
                          : getText('bankAccount')
                    }
                    value={formData.paymentDetails}
                    onChange={handleChange('paymentDetails')}
                    error={!!errors.paymentDetails}
                    helperText={errors.paymentDetails}
                    required={formData.paymentMethod !== 'twint'}
                    placeholder={
                      formData.paymentMethod === 'credit_card'
                        ? '1234 5678 9012 3456'
                        : ''
                    }
                  />
                </Grid>
              )}

              {/* Error Alert */}
              {errors.submit && (
                <Grid item xs={12}>
                  <Alert severity="error">{errors.submit}</Alert>
                </Grid>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ minWidth: 200, textTransform: 'none' }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {getText('processing')}
                      </>
                    ) : (
                      getText('payNow')
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
